import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormGroup } from '@angular/forms';

import { CommonModule } from '@angular/common';
import { Dropdown } from '@com-delta-omnichannel/mach-core-js';
import { Subscription } from 'rxjs';
import {
  AirportDropDownList,
  CheckInTextData,
  CheckInWidget,
  CheckInWidgetFormType
} from '../../models/check-in-widget.interfaces';
import {
  DEFAULTS,
  FORM_FIELD_NAMES
} from '../../models/constants/check-in-constants';
import { CheckInDropdownService } from '../../services/check-in-dropdown.service';
import { CheckInFormSubscriptionService } from '../../services/check-in-form-subscription.service';
import { CheckInFormValidatorService } from '../../services/check-in-form-validator.service';
import { CheckInWidgetInitializationService } from '../../services/check-in-widget-initialization.service';

@Component({
  selector: 'idp-check-in-widget-base',
  standalone: true,
  imports: [CommonModule],
  template: ''
})
export class CheckInWidgetBaseComponent implements OnDestroy {
  @Input() content = {} as CheckInWidget;
  @Input() checkInWidgetForm!: FormGroup<CheckInWidgetFormType>;
  @Input() resetErrorsTrigger = 0;
  @Output() openModalEmit = new EventEmitter<boolean>();
  @Output() airportSelected$ = new EventEmitter<AirportDropDownList>();
  @Output() closeModal = new EventEmitter<void>();
  @Input() selectedAirport: AirportDropDownList = {
    code: DEFAULTS.EMPTY_STRING,
    name: DEFAULTS.EMPTY_STRING
  };

  @Output() analyticsEvent = new EventEmitter<HTMLElement>();

  @ViewChild('tripByValueInput')
  tripByValueInput!: ElementRef<HTMLInputElement>;

  @ViewChild('lastNameInput')
  lastNameInput!: ElementRef<HTMLInputElement>;

  @ViewChild('fromAirportInput')
  fromAirportInput!: ElementRef<HTMLInputElement>;

  airportCode = signal<string>('');
  isSubmitted = signal<boolean>(false);
  dropDownCheckInOptions!: Dropdown;
  initService = inject(CheckInWidgetInitializationService);
  validatorService = inject(CheckInFormValidatorService);
  subscriptionService = inject(CheckInFormSubscriptionService);
  dropdownService = inject(CheckInDropdownService);
  isIntentionalSubmit = false;
  checkInTextData = {} as CheckInTextData;
  protected formSubscription?: Subscription;

  setFocus(element: HTMLInputElement) {
    if (element) {
      requestAnimationFrame(() => {
        element.removeAttribute('readonly');
        element.removeAttribute('disabled');
        element.tabIndex = 0;
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  setupFormSubscriptions(content: CheckInWidget): void {
    this.formSubscription = this.subscriptionService.setupFormSubscriptions(
      this.checkInWidgetForm,
      content,
      () => this.resetFormState(),
      () =>
        this.initService.errorValidation(
          this.formCheckIn,
          content,
          this.checkInTextData?.title
        ),
      () => this.isSubmitted()
    );
  }

  get formCheckIn(): CheckInWidgetFormType {
    return this.checkInWidgetForm.controls;
  }

  resetFormState(): void {
    this.isSubmitted.set(false);
    this.initService.resetErrorState();
  }

  setAirportSubject(changes: SimpleChanges): void {
    if (changes['selectedAirport']?.currentValue) {
      const code = changes['selectedAirport'].currentValue.code;
      this.airportCode.set(code);
      this.checkInWidgetForm
        .get(FORM_FIELD_NAMES.ORIGIN_AIRPORT_CODE)
        ?.setValue(code);
    }
  }

  initializeFreshComponent(content: CheckInWidget): void {
    this.resetFormState();
    this.airportCode.set('');
    this.resetCheckInTextData(content);
  }

  protected resetCheckInTextData(content: CheckInWidget): void {
    if (content.inputSelectedDropdownOption?.length > 0) {
      this.checkInTextData = content.inputSelectedDropdownOption[0];
      this.initService.updateVisibilityFlags(this.checkInTextData.title);
    }
  }

  setFlags(): void {
    this.isSubmitted.set(true);
    this.isIntentionalSubmit = false;
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }
}
